import React, { FC, useState } from 'react';
import { TbBriefcase, TbChevronRight, TbHeadset, TbUserPlus } from 'react-icons/tb';

import { Box, Group, Stack, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import { ContextModalProps } from '@mantine/modals';

import { InvitableCompanyList } from '@diagram/components/invitable-company-list/InvitableCompanyList';
import { ChatService } from '@diagram/services/ChatService';
import { SidebarService } from '@diagram/services/SidebarService';
import { SupportService } from '@diagram/services/SupportService';
import { SidebarType } from '@diagram/state/sidebar';

import { CompanyProfile } from 'models';

import { ModalDescription } from 'components/modals/ModalDescription';
import { ModalTitle } from 'components/modals/ModalTitle';

import cx from './SignOffInvite.module.scss';

const SIGN_OFF_REQUEST_MESSAGE =
    'Hi! Could you review the bill of materials for this design and give it your sign-off?';
const MISSING_COMPANY_MESSAGE =
    '<p>Hi! I would like a company to sign off the bill of materials for this design, but I cannot find them in the list of invitable companies. Could you help me invite them?</p>';

const SignOffInvite: FC<ContextModalProps> = ({ context, id }) => {
    const [step, setStep] = useState<'choose' | 'company'>('choose');

    const invitePerson = () => {
        context.closeModal(id);
        SidebarService.open(SidebarType.SHARE);
    };

    const messageSupport = () => {
        context.closeModal(id);
        SupportService.openSupportChannel(`<p>${SIGN_OFF_REQUEST_MESSAGE}</p>`, 'sign-off');
    };

    const askSupportToInviteCompany = () => {
        context.closeModal(id);
        SupportService.openSupportChannel(MISSING_COMPANY_MESSAGE, 'sign-off-missing-company');
    };

    const inviteCompany = (company: CompanyProfile) => {
        context.closeModal(id);
        ChatService.createChannelForCompany(company, { message: SIGN_OFF_REQUEST_MESSAGE }).then();
    };

    if (step === 'company') {
        return (
            <Stack gap="xs">
                <ModalTitle align="left">Invite a company</ModalTitle>
                <ModalDescription>
                    Start a chat with a company that offers in-app support. They can review the bill of materials and
                    tick their sign-off checks.
                </ModalDescription>

                <InvitableCompanyList onSelect={inviteCompany} />

                <OptionRow
                    icon={<TbHeadset size={16} />}
                    title="Company not here?"
                    description="Ask expert support to invite them"
                    onClick={askSupportToInviteCompany}
                />
            </Stack>
        );
    }

    return (
        <Stack gap="xs">
            <ModalTitle align="left">Ask for sign-off</ModalTitle>
            <ModalDescription>
                Sign-off happens right in this diagram. Invite the people who should review the bill of materials.
            </ModalDescription>

            <Stack gap={4}>
                <OptionRow
                    icon={<TbUserPlus size={16} />}
                    title="Invite a person"
                    description="Share this diagram with a collaborator"
                    onClick={invitePerson}
                />
                <OptionRow
                    icon={<TbBriefcase size={16} />}
                    title="Invite a company"
                    description="Start a chat with a company that offers in-app support"
                    onClick={() => setStep('company')}
                />
                <OptionRow
                    icon={<TbHeadset size={16} />}
                    title="Ask expert support"
                    description="Ask our engineers to help with the sign-off"
                    onClick={messageSupport}
                />
            </Stack>
        </Stack>
    );
};

const OptionRow: FC<{
    icon: React.ReactNode;
    title: string;
    description: string;
    onClick: () => void;
}> = ({ icon, title, description, onClick }) => (
    <UnstyledButton onClick={onClick} p="xs" className={cx.row}>
        <Group justify="space-between" wrap="nowrap" gap="xs">
            <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                <ThemeIcon variant="light" color="gray" radius="xl" size={28}>
                    {icon}
                </ThemeIcon>
                <Box style={{ minWidth: 0 }}>
                    <Text size="sm" fw={500}>
                        {title}
                    </Text>
                    <Text size="xs" c="dimmed">
                        {description}
                    </Text>
                </Box>
            </Group>
            <TbChevronRight />
        </Group>
    </UnstyledButton>
);

export { SignOffInvite };
